import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import articles, { type ArticleData } from "@/mocks/articleData";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

function getArticleFromPath(pathname: string): ArticleData | null {
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  return articles[slug] || null;
}

function ArticleHero({ article }: { article: ArticleData }) {
  return (
    <section className="relative w-full min-h-[500px] md:min-h-[600px] flex items-center">
      <div className="absolute inset-0">
        <img
          src={article.heroImageUrl}
          alt={article.metaTitle}
          title={article.metaTitle}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10 pt-20">
          <div className="flex flex-col gap-6 lg:max-w-2xl">
            <span className="inline-block bg-accent-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider w-fit">
              2026 {article.cluster === "guide" ? "Guide" : article.cluster === "comparison" ? "Comparison" : article.cluster === "commercial" ? "Resource" : "Article"}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-tight">
              <span className="font-bold block">{article.title.split(":")[0]}</span>
              {article.title.includes(":") && (
                <span className="font-light italic block mt-1">
                  {article.title.split(":").slice(1).join(":")}
                </span>
              )}
            </h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white text-foreground-950 px-7 py-3 rounded-full text-base font-semibold hover:bg-accent-100 transition-all duration-200 cursor-pointer whitespace-nowrap w-fit"
            >
              <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
          <div className="lg:max-w-sm lg:pb-6">
            <p className="text-white/85 text-base leading-relaxed">
              {article.metaDescription}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ArticleContent({ article }: { article: ArticleData }) {
  return (
    <section className="w-full py-16 md:py-24 bg-accent-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block bg-white px-5 py-2 rounded-full text-sm font-semibold text-foreground-800 mb-6">
            {article.cluster === "guide" ? "Guide" : article.cluster === "comparison" ? "Comparison" : article.cluster === "commercial" ? "Resource" : "Article"}
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 leading-tight mb-8">
            Complete Overview
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            {article.sections.map((section, idx) => (
              <div key={idx} className="bg-white/60 rounded-xl p-4">
                <span className="text-xs font-semibold text-accent-700 uppercase tracking-wider">Jump to</span>
                <p className="text-sm text-foreground-800 mt-1">{section.h2}</p>
              </div>
            ))}
            <div className="bg-white/60 rounded-xl p-4">
              <span className="text-xs font-semibold text-accent-700 uppercase tracking-wider">Jump to</span>
              <p className="text-sm text-foreground-800 mt-1">FAQ</p>
            </div>
          </div>

          <div className="prose-custom space-y-10">
            <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
              {article.intro}
            </p>

            {article.sections.map((section, idx) => (
              <div key={idx}>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                  {section.h2}
                </h2>
                <p className="text-foreground-700 leading-relaxed text-base">
                  {section.content}
                </p>
              </div>
            ))}

            <div className="bg-white/70 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
                {article.comparisonTable.title}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-foreground-200">
                      {article.comparisonTable.headers.map((h, i) => (
                        <th key={i} className="text-left py-3 px-3 font-semibold text-foreground-900 text-xs uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {article.comparisonTable.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-foreground-100">
                        {row.map((cell, ci) => (
                          <td key={ci} className={`py-3 px-3 ${ci === 0 ? "font-medium text-foreground-900" : "text-foreground-600"}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/70 rounded-2xl p-6 md:p-8">
                <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-4">
                  {article.prosConsTable.title}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-accent-200">
                        <th className="text-left py-2 px-3 font-semibold text-accent-800 text-xs uppercase">Pros</th>
                        <th className="text-left py-2 px-3 font-semibold text-secondary-700 text-xs uppercase">Cons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {article.prosConsTable.rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-foreground-100">
                          <td className="py-2.5 px-3 text-foreground-700 text-xs leading-relaxed">{row[0]}</td>
                          <td className="py-2.5 px-3 text-foreground-600 text-xs leading-relaxed">{row[1]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white/70 rounded-2xl p-6 md:p-8">
                <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-4">
                  {article.useCaseTable.title}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-accent-200">
                        {article.useCaseTable.headers.map((h, i) => (
                          <th key={i} className="text-left py-2 px-2 font-semibold text-foreground-900 text-xs uppercase tracking-wider">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {article.useCaseTable.rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-foreground-100">
                          {row.map((cell, ci) => (
                            <td key={ci} className={`py-2.5 px-2 text-xs leading-relaxed ${ci === 1 ? "font-semibold text-accent-800" : "text-foreground-700"}`}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="bg-white/70 rounded-2xl p-6 md:p-8">
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
                Want to explore more about the OOPBuy spreadsheet? Check out our{" "}
                {article.internalLinks.map((link, idx) => (
                  <span key={idx}>
                    {idx > 0 && idx < article.internalLinks.length - 1 && ", "}
                    {idx > 0 && idx === article.internalLinks.length - 1 && " or "}
                    <Link to={link.to} className="text-accent-700 font-semibold underline hover:text-accent-800 cursor-pointer">
                      {link.text}
                    </Link>
                  </span>
                ))}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ArticleFaq({ article }: { article: ArticleData }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full py-16 md:py-24 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-10 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {article.faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-50 transition-colors duration-150"
                >
                  <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                    {faq.q}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                      openIndex === idx ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openIndex === idx && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ArticleCta({ article }: { article: ArticleData }) {
  return (
    <section className="w-full flex flex-col lg:flex-row min-h-[400px] md:min-h-[480px]">
      <div className="lg:w-1/2 relative overflow-hidden min-h-[260px] lg:min-h-[480px]">
        <img
          src={article.ctaImageUrl}
          alt="OOPBuy Spreadsheet system"
          title="Get the OOPBuy Spreadsheet System"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/15"></div>
      </div>
      <div className="lg:w-1/2 bg-background-50 flex items-center justify-center p-8 md:p-14">
        <div className="max-w-md text-center flex flex-col items-center gap-5">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 leading-tight">
            Ready to Scale?
          </h2>
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
            Now that you understand how the OOPBuy spreadsheet works, it is time to put it to work. Access the full system, unlock advanced automation, and start tracking products like a professional reseller today.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 bg-foreground-950 text-white px-8 py-3.5 rounded-full text-base font-semibold hover:bg-accent-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Unlock Full System
            <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function ArticlePage() {
  const location = useLocation();
  const article = getArticleFromPath(location.pathname);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    if (!article) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || "https://oopbuyspreadsheets.pics";
    const pageUrl = siteUrl + location.pathname;

    document.title = article.metaTitle;

    const setMetaTag = (name: string, content: string, property?: string) => {
      const attr = property ? "property" : "name";
      const selector = property
        ? `meta[property="${property}"]`
        : `meta[name="${name}"]`;
      let metaEl = document.querySelector(selector) as HTMLMetaElement | null;
      if (!metaEl) {
        metaEl = document.createElement("meta");
        if (property) {
          metaEl.setAttribute("property", property);
        } else {
          metaEl.setAttribute("name", name);
        }
        document.head.appendChild(metaEl);
      }
      metaEl.setAttribute("content", content);
    };

    setMetaTag("description", article.metaDescription);
    setMetaTag("keywords", article.metaKeywords);
    setMetaTag("og:title", article.metaTitle, "og:title");
    setMetaTag("og:description", article.metaDescription, "og:description");
    setMetaTag("og:url", pageUrl, "og:url");
    setMetaTag("og:type", "article", "og:type");

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", pageUrl);

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.metaTitle,
      "description": article.metaDescription,
      "url": pageUrl,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": pageUrl
      },
      "author": {
        "@type": "Person",
        "name": "OOPBuy Spreadsheet Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "OOPBuy Spreadsheet",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg"
        }
      },
      "datePublished": "2026-06-22",
      "dateModified": "2026-06-22",
      "image": article.heroImageUrl
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": article.faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a,
        },
      })),
    };

    const schemas = [
      { id: "article-schema", data: articleSchema },
      { id: "faq-schema", data: faqSchema },
    ];

    schemas.forEach(({ id, data }) => {
      let scriptEl = document.getElementById(id);
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.id = id;
        scriptEl.type = "application/ld+json";
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(data);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-accent-700 font-semibold underline cursor-pointer">Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <ArticleHero article={article} />
        <ArticleContent article={article} />
        <ArticleFaq article={article} />
        <ArticleCta article={article} />
      </main>
      <Footer />
    </div>
  );
}